import * as React from "react"
import {ExtraChargeType} from "../graphql/queries"
import {Form, FormButton, Icon, Table, TableHeader} from "semantic-ui-react"
import {ExtraChargeForm, ExtraChargeTypeLabels} from "./ExtraChargeForm"
import {Link} from "react-router-dom"
import {formatMoney} from "../model/charges"
import {MapState} from "./BookingDetails"

/**
 * Either a manual extra charge (row in the database) or an automatic extra charge (no row in
 * database, cannot be edited).
 */
export interface AnyExtraCharge {
  amount: number,
  description: string,
  type: ExtraChargeType
}

export interface ExtraCharge extends AnyExtraCharge {
  id?: string
  amount: number
  description: string
  type: ExtraChargeType
  invoice?: {
    id: string
    number: string
    xeroInvoiceReady: boolean
  } | null
}

interface ExtraChargeListProps {
  state: ExtraChargeListState
  mapState: MapState<ExtraChargeListState> | null
  isAdmin: boolean
  disabled: boolean
  automaticExtraCharges: Array<AnyExtraCharge>
}

export interface ExtraChargeListState {
  isEditing: boolean
  index: number | null
  extraCharges: Array<ExtraCharge>
}

export function extraChargesLabel(extraCharges: Array<AnyExtraCharge>) {
  if (extraCharges.length === 0) {
    return ""
  } else if (extraCharges.every((x) => x.amount < 0)) {
    return "Credits"
  } else if (extraCharges.every((x) => x.amount > 0)) {
    return "Extra Charges"
  } else {
    return "Extra Charges and Credits"
  }
}

export function ExtraChargeList(props: ExtraChargeListProps) {
  function render() {
    const {isEditing, index, extraCharges} = props.state
    const {isAdmin, disabled, mapState, automaticExtraCharges} = props

    const allExtraCharges = [...extraCharges, ...automaticExtraCharges];

    return (
      <>
        {(allExtraCharges.length > 0 || (isEditing && mapState)) && (
          <Form.Field>
            <label>{extraChargesLabel(allExtraCharges)}</label>
            {allExtraCharges.length > 0 && (
              <Table compact="very" unstackable>
                <TableHeader>
                  <Table.Row>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Amount</Table.HeaderCell>
                    <Table.HeaderCell>Description</Table.HeaderCell>
                    {/* TODO Maybe merge the Invoice and Edit/Delete columns? */}
                    {isAdmin && <Table.HeaderCell>Invoice</Table.HeaderCell>}
                    {mapState && <Table.HeaderCell>Edit</Table.HeaderCell>}
                    {mapState && <Table.HeaderCell>Delete</Table.HeaderCell>}
                  </Table.Row>
                </TableHeader>
                <Table.Body>
                  {automaticExtraCharges.map(charge => {
                    return (
                      <Table.Row>
                        <Table.Cell>{ExtraChargeTypeLabels.get(charge.type)}</Table.Cell>
                        <Table.Cell>{formatMoney(charge.amount)}</Table.Cell>
                        <Table.Cell>{charge.description}</Table.Cell>
                        {isAdmin && <Table.Cell> Not invoiced </Table.Cell>}
                        {mapState && <Table.Cell/>}
                        {mapState && <Table.Cell/>}
                      </Table.Row>
                    )
                  })}
                  {extraCharges.map((charge, index) => {
                    const {invoice} = charge
                    const isEditable = !invoice && !isEditing
                    return (
                      <Table.Row key={index} warning={index === props.state.index}>
                        <Table.Cell>{ExtraChargeTypeLabels.get(charge.type)}</Table.Cell>
                        <Table.Cell>{formatMoney(charge.amount)}</Table.Cell>
                        <Table.Cell>{charge.description}</Table.Cell>
                        {/* TODO make invoice page work even when Xero invoice is generating */}
                        {isAdmin && (
                          <Table.Cell>
                            {invoice ? (
                              invoice.xeroInvoiceReady ? (
                                <Link to={"/invoice/" + invoice.id} replace={false}>
                                  {invoice.number}
                                </Link>
                              ) : (
                                invoice.number
                              )
                            ) : (
                              "Not invoiced"
                            )}
                          </Table.Cell>
                        )}
                        {mapState && (
                          <Table.Cell>
                            {isEditable && (
                              <Icon
                                name="write"
                                link
                                disabled={disabled}
                                onClick={() => {
                                  if (disabled) {
                                    return
                                  }
                                  mapState((state) => ({...state, isEditing: true, index}))
                                }}
                              />
                            )}
                          </Table.Cell>
                        )}
                        {mapState && (
                          <Table.Cell>
                            {isEditable && (
                              <Icon
                                name="close"
                                link
                                disabled={disabled}
                                onClick={() => {
                                  if (disabled) {
                                    return
                                  }
                                  mapState((state) => {
                                    const charges = [...state.extraCharges]
                                    charges.splice(index, 1)
                                    return {...state, extraCharges: charges}
                                  })
                                }}
                              />
                            )}
                          </Table.Cell>
                        )}
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table>
            )}
            {isEditing &&
              mapState && (
                <ExtraChargeForm
                  disabled={disabled}
                  data={index !== null ? extraCharges[index] : null}
                  onSave={(data) => {
                    mapState((state) => {
                      const charges = [...state.extraCharges]
                      if (index !== null) {
                        charges[index] = {...charges[index], ...data}
                      } else {
                        charges.push(data)
                      }
                      return {
                        isEditing: false,
                        index: null,
                        extraCharges: charges,
                      }
                    })
                  }}
                  onReset={() => {
                    mapState((state) => ({
                      ...state,
                      isEditing: false,
                      index: null,
                    }))
                  }}
                />
              )}
          </Form.Field>
        )}
        {!isEditing &&
          mapState &&
          isAdmin && (
            <FormButton
              disabled={disabled}
              icon="plus"
              labelPosition="left"
              onClick={() => {
                mapState((state) => ({
                  ...state,
                  isEditing: true,
                  index: null,
                }))
              }}
              content="Add extra charge"
            />
          )}
      </>
    )
  }

  return render()
}



// WEBPACK FOOTER //
// ./src/components/ExtraChargeList.tsx